import { useMemo, useRef } from 'react';
import { groupProgress } from '../../extensions/bundled/task-manager/boardProgress';

const sameItems = (a, b) => a && b && a.length === b.length && a.every((x, i) => x === b[i]);
const sameProgress = (a, b) => a && b && a.total === b.total && a.done === b.done && a.open === b.open && a.pct === b.pct;

/**
 * Per-list tasks and progress for an open board, with STABLE identities (owner,
 * 13.10.2026: dragging between lists lagged on 80+ task boards). A drag step
 * changes one or two lists; every other list gets back the very same array and
 * progress object it had, so its memoised Column / ListSection skips the render.
 *
 * `order(tasks)` sorts a list's visible tasks; `visible(task)` is the filter.
 * Returns Map<groupId, { tasks, progress }>.
 */
export default function useGroupTasks(board, groups, visible, order) {
  const cacheRef = useRef(new Map());
  return useMemo(() => {
    const prev = cacheRef.current;
    const next = new Map();
    if (!board) {
      cacheRef.current = next;
      return next;
    }
    const byGroup = new Map(groups.map((g) => [g.id, []]));
    board.tasks.forEach((t) => {
      const bucket = byGroup.get(t.groupId);
      if (bucket && visible(t)) bucket.push(t);
    });
    groups.forEach((g) => {
      const tasks = order(byGroup.get(g.id));
      const progress = groupProgress(board, g.id);
      const old = prev.get(g.id);
      next.set(g.id, {
        tasks: sameItems(old?.tasks, tasks) ? old.tasks : tasks,
        progress: sameProgress(old?.progress, progress) ? old.progress : progress,
      });
    });
    cacheRef.current = next;
    return next;
  }, [board, groups, visible, order]);
}
